import type { FC } from 'react';

import { useTranslation } from '@crehana/i18n';
import Download from '@crehana/icons/outline/Download';
import ExternalLink from '@crehana/icons/outline/ExternalLink';
import FileAscendingSorting from '@crehana/icons/pictograms/FileAscendingSorting';
import FileUpload from '@crehana/icons/pictograms/FileUpload';
import SpreadsheetFile from '@crehana/icons/pictograms/SpreadsheetFile';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors } from '@crehana/ui';

import locales, {
  ADMIN_DASHBOARD_INSTRUCTIONS,
  type TInstructionsKeys,
} from './i18n';
import type { Instruction, InstructionsProps } from './types';

const { baseLighter, baseMain, white } = colors;

export const defaultInstructions: Instruction[] = [
  {
    icon: SpreadsheetFile,
    label: 'INSTRUCTIONS_STEP_1',
    buttonLabel: 'INSTRUCTIONS_STEP_1_LINK',
  },
  {
    icon: FileUpload,
    label: 'INSTRUCTIONS_STEP_2',
  },
  {
    icon: FileAscendingSorting,
    label: 'INSTRUCTIONS_STEP_3',
  },
];

const Instructions: FC<InstructionsProps> = ({
  isDark,
  instructions = defaultInstructions,
}) => {
  const { addResourceBundle, t } = useTranslation<TInstructionsKeys>(
    ADMIN_DASHBOARD_INSTRUCTIONS,
  );

  addResourceBundle(locales);

  for (let i = 0; i < defaultInstructions.length; i++) {
    defaultInstructions[i].label = t(
      defaultInstructions[i].label as TInstructionsKeys,
    );
    defaultInstructions[i].buttonLabel = t(
      defaultInstructions[i].buttonLabel as TInstructionsKeys,
    );
  }

  const iconColor = isDark ? white : baseMain;

  const hover = isDark
    ? 'text-secondary-main hover:text-secondary-light'
    : 'text-primary-main hover:text-primary-lighter';

  return (
    <div className="tw-grid tw-grid-cols-3 tw-gap-6.4">
      {instructions.map((instruction, i) => {
        const {
          icon: Icon,
          label,
          buttonLabel,
          href,
          isExternal,
          onClick,
          tourSelector,
        } = instruction;

        const linkProps = {
          ...(onClick && { onClick }),
          ...(!onClick && { href, target: isExternal ? '_blank' : undefined }),
        };

        return (
          <div
            data-tour={tourSelector}
            className="flex flex-col items-center"
            key={`instruction-${label}`}
          >
            <div className="flex items-start justify-center w-full pb-16">
              <div
                css={{ lineHeight: 'normal' }}
                className="tw-font-subtitle5 flex justify-center items-center w-20 h-20 rounded-full bg-base-lighter text-white mr-8"
              >
                {i + 1}
              </div>
              {Icon && (
                <Icon
                  size={48}
                  color={iconColor}
                  secondaryColor={baseLighter}
                />
              )}
            </div>
            <div className="flex flex-col items-center w-full">
              <p className="tw-font-body4 dark:tw-text-neutral-light-100 text-center whitespace-pre tw-mb-0.4">
                {label}
              </p>
              {Boolean(buttonLabel) && (
                <a
                  className={`${hover} flex items-center tw-font-subtitle5`}
                  {...linkProps}
                >
                  {!isExternal && (
                    <Download color="currentColor" size={20} className="mr-4" />
                  )}
                  {buttonLabel}
                  {isExternal && (
                    <ExternalLink
                      color="currentColor"
                      size={18}
                      className="ml-4 self-center"
                    />
                  )}
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Instructions;
